// PATCH /api/me/settings: aktives Land, Laender-Reihenfolge, Sichtbarkeit.
//
// Dieselbe Positivliste wie im Server (api/src/routes/me.ts). Jeder Aufruf
// schickt nur das, was sich geaendert hat; der Server merged es flach in
// users.settings.

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "./api.js";

export interface LandSichtbarkeit {
  list: boolean;
  search: boolean;
}

export interface Einstellungen {
  activeCountry?: string;
  countryOrder?: string[];
  countryVisibility?: Record<string, LandSichtbarkeit>;
  /** In welche Sammlung der Hauptscreen gerade schreibt (Phase 6). */
  activeCollectionId?: number;
  /**
   * Nur wirksam, wenn die aktive Sammlung eine geteilte ist: neue Funde
   * zusaetzlich in die eigene (persoenliche) Sammlung eintragen.
   */
  spiegleInEigene?: boolean;

  // Ab hier: Einstellungen-Bildschirm, Phase 11 (docs/reference/
  // einstellungen-oben.png, einstellungen-unten.png, 02.09.2026). Jeder
  // Schalter fehlt bei einem neuen Nutzer (kein Eintrag in users.settings)
  // - die Vorgabe entspricht deshalb genau dem Zustand im Referenz-
  // Screenshot, nicht zwingend "alles aus".
  /** Punkteanzeigen (Ring, Zeile unter einem Treffer) ausblenden. Vorgabe (Screenshot): aus. */
  punkteAusblenden?: boolean;
  /** Kennzeichen mit status "historisch" werden bei der Suche nicht mehr getroffen. Vorgabe (Screenshot): aus. */
  historischeAusblenden?: boolean;
  /** Kennzeichen mit scope "diplomatic" werden bei der Suche getroffen. Vorgabe (Screenshot): an (anders als die beiden oberen Schalter positiv formuliert). */
  diplomatenAnzeigen?: boolean;
  /** Aus: die Suche trifft nur noch das aktive Land statt aller sichtbaren. Vorgabe (Screenshot): an. */
  globaleSuche?: boolean;
  /** Eingabefeld nach dem Setzen des Sterns (neuer Fund) leeren. Vorgabe (Screenshot): an. */
  eingabeLoeschenBeiNeu?: boolean;
  /**
   * Eingabefeld leeren, wenn der aktuelle Treffer schon gesammelt ist -
   * aber erst nach dieser Wartezeit ("...", docs/reference/einstellungen-
   * sekunden-menue.png). 0 = "aus", der Screenshot selbst zeigte 4
   * markiert, das ist aber der Wert EINES Nutzers, keine erkennbare
   * App-Vorgabe - Vorgabe hier deshalb 0.
   */
  eingabeLoeschenBeiVorhandenSekunden?: 0 | 1 | 2 | 3 | 4;
  /**
   * Rein optisch, ohne Wirkung: dunkler Modus selbst ist bewusst
   * zurueckgestellt (Nutzerentscheidung, heller Modus bevorzugt). Der
   * Schalter existiert nur, damit die Seite 1:1 aussieht.
   */
  dunklerModus?: boolean;
}

export function useEinstellungenSpeichern() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (patch: Einstellungen) =>
      api<{ settings: Record<string, unknown> }>("/me/settings", { method: "PATCH", body: patch }),
    onSuccess: () => {
      // /me traegt settings mit; nach dem Speichern neu ziehen statt selbst
      // zusammenzubauen, damit Server und Client nie auseinanderlaufen.
      void queryClient.invalidateQueries({ queryKey: ["me"] });
    },
  });
}
